/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ButtonBase } from '../button/ButtonBase';
import { InputBase } from '../input/InputBase';
import { Add } from '../private/icons/Add';
import { Subtract } from '../private/icons/Subtract';

const capMin = (min, value) => (value < min ? min : value);
const capMax = (max, value) => (value > max ? max : value);

const ADD = 'add';
const SUBTRACT = 'subtract';

const Stepper = ({
  min,
  max,
  step,
  id,
  value: valueProp,
  disabled,
  decrementTitle,
  incrementTitle,
  onChange,
  ...other
}) => {

  const [disabledMinus, setDisabledMinus] = useState(valueProp <= min || disabled);
  const [disabledPlus, setDisabledPlus] = useState(valueProp >= max || disabled);

  const [value, setValue] = useState(valueProp || min);
  const updateValue = (newValue) => {
    setValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const clickButtonHandler = (increment) => (event) => {
    const newValue = Number(value) + increment;
    updateValue(capMax(max, capMin(min, newValue)));
  };

  const inputChangeHandler = (event) => {
    if (event.target.checkValidity()) {
      updateValue(event.target.value);
    }
  };

  const keyDownHandler = (event) => {
    if (event.keyCode === 40) { // Down key
      const newValue = Number(value) - step;
      updateValue(capMax(max, capMin(min, newValue)));
    } else if (event.keyCode === 38) { // Up key
      const newValue = Number(value) + step;
      updateValue(capMax(max, capMin(min, newValue)));
    }
  };

  useEffect(() => {
    if (!disabled) {
      setDisabledMinus(value <= min);
      setDisabledPlus(value >= max);
    }
  }, [disabled, value, min, max]);

  const buttonClasses = (isDisabled, button) => classNames('sui-w-11 sui-h-11 sui-border-1 sui-border-solid', {
    'sui-rounded-l-base': button === 'subtract',
    'sui-rounded-r-base': button === 'add',
    'sui-text-subtle sui-border-strong hover:sui-bg-subtle hover:sui-border-accent hover:sui-text-primary hover:sui-z-10': !isDisabled,
    'focus-visible:sui-z-20 focus-visible:sui-outline focus-visible:sui-outline-4 focus-visible:sui-outline-focus focus-visible:sui-bg-inverse focus-visible:sui-text-inverse': !isDisabled,
    '!sui-bg-inactive !sui-text-inactive sui-outline-none sui-border-inactive sui-z-0': isDisabled // Force background and text color due to Firefox bug https://thd.atlassian.net/browse/CON-741
  });

  const rootInputClasses = classNames('focus:sui-z-10 hover:sui-z-10 sui-rounded-none sui--mx-1px sui-w-[60px]', {
    'sui-border-inactive': disabled,
    'hover:!sui-border-accent !sui-border-strong': !disabled
  });

  const iconClasses = 'sui-align-middle';

  return (
    <div className="sui-flex">
      <ButtonBase
        unstyled
        className={buttonClasses(disabledPlus, SUBTRACT)}
        onClick={clickButtonHandler(-step)}
        disabled={disabledMinus}
        aria-label={decrementTitle}
        title={decrementTitle}
      >
        <Subtract size="small" className={iconClasses} />
      </ButtonBase>
      <InputBase
        id={id}
        pattern="[0-9]*"
        value={value}
        disabled={disabled}
        onChange={inputChangeHandler}
        // onFocus={focusHandler}
        // onBlur={blurHandler}
        onKeyDown={keyDownHandler}
        classes={{ root: rootInputClasses, input: 'sui-text-center' }}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        disableStatus
        {...other}
      />
      <ButtonBase
        unstyled
        className={buttonClasses(disabledPlus, ADD)}
        onClick={clickButtonHandler(step)}
        disabled={disabledPlus}
        aria-label={incrementTitle}
        title={incrementTitle}
      >
        <Add size="small" className={iconClasses} />
      </ButtonBase>
    </div>
  );

};

Stepper.displayName = 'Stepper';

Stepper.propTypes = {
  /**
   * Minimum value allowed when using the left button
   */
  min: PropTypes.number.isRequired,
  /**
   * Maximum value allowed using the right button
   */
  max: PropTypes.number.isRequired,
  /**
   * Increment to be applied when using the buttons
   */
  step: PropTypes.number,
  /**
   * If true, the buttons and text input will be inactive
   */
  disabled: PropTypes.bool,
  /**
   * id given to the text input
   */
  id: PropTypes.string,
  /**
   * `Stepper` value.
   */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Event fired when the user changes the value using the buttons or typing into the text input
   */
  onChange: PropTypes.func,
  /**
   * Title to be set on the right button
   */
  incrementTitle: PropTypes.string,
  /**
   * Title to be set on the left button
   */
  decrementTitle: PropTypes.string,
};

Stepper.defaultProps = {
  step: 1,
  incrementTitle: 'Increment',
  decrementTitle: 'Decrement'
};

export { Stepper };